import { Component } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { WithStyles, withStyles } from '@mui/styles';
import queryString, { ParsedQuery } from 'query-string';

// Contexts
import { withNavigation, withTenant, withTranslate } from '../../core/TenantProvider';
import { getConfigProperty } from '../../core/EnvironmentConfig/EnvironmentConfig';
// Components - Atoms, Organisms,Containers
import APStepContainer from './../../components/organisms/APStepContainer';
import APBackdropLoader from '../../components/atoms/APBackdropLoader';
import APAlertMessage from '../../components/atoms/APAlertMessage';
// Files in the same folder
import { BECOME_PARTNER_FORM } from './definitions/becomepartner';
import BecomePartnerComplete from './BecomePartnerComplete';
import BecomePartnerFailed from './BecomePartnerFailed';
import BecomePartnerContainerStyles from './styles/BecomePartnerContainer.style';
import COUNTRIES_ALL_DATA_JSON_EN from '../../core/assets/CountriesList/countriesOptionAllData.en.json';
// Types
import { BecomePartnerForm } from '../../core/types/FormTypes';
// Reducer
import { ReducerState } from './../../redux/modules';
import { actions as servicesDowntimeAction } from '../../redux/modules/servicesDowntime';
// Utils
import { getCountryISO2 } from '../../components/organisms/APBankAccountLookup/utils/isoMapper';
import GetBasicDetails from '../../core/utils/GetBasicDetails';
import EncodeData from '../../core/utils/EncodeDecodeData';
import { DialingCodeObj } from '../../core/utils/GetDialingCode/GetDialingCode';
import { GetDialingCodeWithoutAuth } from '../../core/utils/GetDialingCode';
import joinArray from '../SettingsContainer/utils/joinArray';
import { OptionObject } from '../../core/utils/GetCountriesData/GetCountriesData';
import { ConvertToSessionCookie } from '../../core/utils/ConvertToSessionCookie/ConvertToSessionCookie';
// Constants
import {
	BECOME_PARTNER_JOURNEY,
	CLIENT_COUNTRY_CODE,
	DISABLED_COUNTRIES_IN_OPTION_LIST,
	REFERRAL_PARTNER_COOKIE
} from '../../core/utils/Constants/Constants';

const {
	basicAuthToken: BASIC_AUTH_TOKEN,
	organisationCode: ORGANISATION
} = GetBasicDetails();

interface BecomePartnerContainerProps extends WithStyles<typeof BecomePartnerContainerStyles> {
	translate: Function,
	loadingState: string,
	settings: any,
	location: any,
	getServicesErrorObj: Function
}

class BecomePartnerContainer extends Component<BecomePartnerContainerProps, {}> {

	state = {
		isComplete: false,
		isLoading: false,
		isError: false,
		isAlertToBeShown: false,
		step: 0,
		steps: [],
		refPartnerAffiliateNumber: null
	};

	static getDerivedStateFromProps(props: BecomePartnerContainerProps, state: any) {
		if (props.loadingState === "LOADED") {
			return {
				...state
			};
		}

		return state;
	}

	constructor(props: BecomePartnerContainerProps) {
		super(props);
		this.onComplete = this.onComplete.bind(this);
		this.onGoToStep = this.onGoToStep.bind(this);
		this.onContinue = this.onContinue.bind(this);
		this.getClientType = this.getClientType.bind(this);
		this.getClientCountries = this.getClientCountries.bind(this);
		this.addOptionalBankAccountDetails = this.addOptionalBankAccountDetails.bind(this);
		this.onBack = this.onBack.bind(this);
		this.getPartnerReferralID = this.getPartnerReferralID.bind(this);
	}

	createPartnerEndPoint: string = getConfigProperty('apiEndPoint')['createPartner'];
	accessToken = BASIC_AUTH_TOKEN;
	axiosDefaults: any = {
		"baseUrl": "",
		"headers": {
			"Access-Control-Allow-Origin": "*",
			"Authorization": `Basic ${this.accessToken}`,
			"Content-Type": "application/json"
		}
	};

	onClear() {
		this.setState({
			complete: false
		});
	}

	onGoToStep(form: any) {

	}

	getClientType = (form: BecomePartnerForm): string[] => {
		let clientType: string[] = [];
		const clientTypesMap = {
			personalClients: "Personal clients",
			onlineMarketSellers: "Online marketplace sellers",
			corporateClients: "Corporate clients",
		};

		clientType = Object.keys(clientTypesMap).filter((key) => form[key]).map((key) => clientTypesMap[key]);

		return clientType;
	}

	getClientCountries = (form: BecomePartnerForm) => {
		let updatedClientCountries: any = [];
		if (form.country) {
			const countryName = Object.fromEntries(Object.entries(CLIENT_COUNTRY_CODE).map(a => a.reverse()));
			updatedClientCountries = form.country.map((clientCountry: string | number) => {
				return countryName[clientCountry];
			});
		}
		return updatedClientCountries;
	}

	addOptionalBankAccountDetails = (form: BecomePartnerForm, payLoad: any) => {
		// Add optional bank account fields
		const accountMatrixFields: any = form.account.matrixFields;
		if (accountMatrixFields.length > 0) {

			const paymentCountryCode = getCountryISO2(form.account.payment_country);

			accountMatrixFields.forEach((field: any) => {
				if (form.account[field].length > 0) {
					payLoad.affdetails[field] = form.account[field];
				}
			});
			const { bank_name, bank_address, bank_account_name, payment_currency, payment_country } = form.account;

			if (bank_name || bank_address || bank_account_name || payment_currency || payment_country) {
				payLoad.affdetails = {
					...payLoad.affdetails,
					...(bank_name && { bank_name }),
					...(bank_address && { bank_address }),
					...(bank_account_name && { bank_account_name }),
					...(payment_currency && { payment_currency }),
					...(payment_country && { payment_country: paymentCountryCode }),
				};
			}
		}

		if (form.companyName) payLoad.affdetails["trading_name"] = form.trading_name;

		return payLoad;
	}

	getPayload = (form: BecomePartnerForm, countryData: any[], clientType: string[], updatedClientCountries: any) => {
		let payLoad = {
			"clientdata": {
				"client_type": joinArray(clientType),
				"client_languages": joinArray(form.language),
				"client_industry": joinArray(form.industry),
				"client_based_countries": joinArray(updatedClientCountries),
				"account_manager_emails": true,
				"business_sector": form.businessSector,
				"business_category": form.businessCategory
			},
			"affdetails":
			{
				"referral_text": "internet",
				"organisation": ORGANISATION,
				"salutation": form.salutation,
				"company": (form.companyName) ? form.companyName : '',
				"firstname": form.firstName,
				"lastname": form.lastName,
				"phone": (form.mobile.select && form.mobile.input) ? `${form.mobile.select}-${form.mobile.input}` : " ",
				"country": (countryData) ? countryData.find(val => val.value === form.address.country).label.trim() : '',
				"postalcode": (form.address.postcode) ? form.address.postcode : '',
				"street": (form.address.line1) ? form.address.line1 : " ",
				"city": (form.address.city) ? form.address.city : " ",
				"state": (form.address.county) ? form.address.county : '',
				"email": form.email.trim(),
				"password": EncodeData(form.password),
				...(form.taxNumber && { "tax_number": (form.taxNumber).toUpperCase() }),
				"referring_aff_tan_no": this.state.refPartnerAffiliateNumber,
				"partnerflowtype": BECOME_PARTNER_JOURNEY
			}
		}

		const updatedPayLoad = this.addOptionalBankAccountDetails(form, payLoad)

		return updatedPayLoad;
	}

	onComplete(form: BecomePartnerForm, step: number, steps: any) {
		const { getServicesErrorObj } = this.props;
		this.setState({
			step: step,
			steps: steps,
			isLoading: true,
			isError: false
		}, () => {

			const clientType: string[] = this.getClientType(form);
			const updatedClientCountries = this.getClientCountries(form);
			const countryData: OptionObject[] = COUNTRIES_ALL_DATA_JSON_EN.options.filter((country) => !DISABLED_COUNTRIES_IN_OPTION_LIST.includes(country.value));

			const payLoad = this.getPayload(form, countryData, clientType, updatedClientCountries);

			axios.defaults.baseURL = this.axiosDefaults.baseURL;
			axios.defaults.headers.post = this.axiosDefaults.headers;
			axios
				.post(this.createPartnerEndPoint, payLoad, { timeout: 10000 }) // If longer than 10sec
				.then(() => {
					this.setState({ isLoading: false, isComplete: true, isError: false });
				})
				.catch((error) => {
					if (error.response?.status === 504 && error.response?.data?.code === '1510') {
						this.setState({ isAlertToBeShown: true, isLoading: false });
					} else {
						getServicesErrorObj(error);
						this.setState({ isLoading: false, isError: true, isEditing: true }, () => {
							console.log(error + ' Error got here');
						});
					}
				});
		});
	}

	getPartnerReferralID() {
		const { location } = this.props;
		const { RefPartner: refPartnerQueryParam }: ParsedQuery<string> = queryString.parse(location.search);
		const refPartnerCookie: string | null = ConvertToSessionCookie(REFERRAL_PARTNER_COOKIE);

		this.setState({
			refPartnerAffiliateNumber: refPartnerQueryParam || refPartnerCookie || null
		})
	}

	componentDidMount() {
		Cookies.remove('register');
		Cookies.set("register", "0");
		Cookies.remove('accountPaymentCountry');
		Cookies.remove('accountPaymentCurrency');
		this.getPartnerReferralID();
	}

	onContinue(step: number, steps: any) {
		this.setState({
			step: step,
			steps: steps
		});
	}

	onBack(step: number, steps: any) {
		this.setState({
			step: step,
			steps: steps
		});
	}

	render() {

		const {
			classes,
			translate,
			settings,
			getServicesErrorObj
		} = this.props;

		const {
			isComplete,
			isError,
			isLoading,
			step,
			steps,
			isAlertToBeShown
		} = this.state;

		const max = steps.length - 1;
		const diallingCodeData: DialingCodeObj = GetDialingCodeWithoutAuth();

		return (
			<div className={classes.BecomePartnerContainer}>

				{isAlertToBeShown && step === max &&
					<APAlertMessage
						className={`serverErrorMessage`}
						title={translate('server.timeout.error.title')}
						message={translate('server.timeout.error.message')}
						severity={"error"}
						icon={'error'}
						hideCloseIcon={false}
						onClose={() => {
							this.setState({
								isAlertToBeShown: false
							})
						}}
					/>
				}
				{isLoading &&
					<APBackdropLoader open={true} />
				}

				{isComplete && (
					<BecomePartnerComplete settings={settings} />
				)}

				{isError && (
					<BecomePartnerFailed settings={settings} />
				)}

				{!isComplete && !isError && (
					<div className={classes.BecomePartnerFormContainer}>
						<div className={classes.BecomePartnerContainerTitle}>
							<h1>{translate('become.partner.heading')}</h1>
							<p>{translate('become.partner.paragraph')}</p>
						</div>
						<div className={classes.BecomePartnerFormSteps}>
							<APStepContainer
								defaults={{
									customerType: 'PFX',
									mobile: { select: diallingCodeData?.dialingCode, input: '' }
								}}
								onComplete={this.onComplete}
								onContinue={this.onContinue}
								onBack={this.onBack}
								onGoToStep={this.onGoToStep}
								steps={BECOME_PARTNER_FORM}
								formName={`becomePartner`}
								getServicesErrorObj={getServicesErrorObj}
							/>
						</div>
					</div>
				)}

			</div>
		);
	}
}

const mapStateToProps = (state: ReducerState) => ({
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	getServicesErrorObj: servicesDowntimeAction.getServicesErrorObj
}, dispatch);


export default connect(mapStateToProps, mapDispatchToProps)(
	withNavigation(
		withTenant(
			withTranslate(
				withStyles(BecomePartnerContainerStyles)(
					BecomePartnerContainer
				)
			)
		)
	)
);

